// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getPageLinkByPageType, useGetLinks } from "@insite/client-framework/Store/Links/LinksSelectors";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Icon from "@insite/mobius/Icon";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import loadAccountTilesData from "@pace/App/Handlers/XNG/AccountTilesData";
import { CustomAppState } from "@pace/App/Store/Reducers";
import { xIsAuthenticated } from "@pace/App/Store/Selectors/GeneralSelectors";
import { xHasRoles } from "@pace/App/Store/Selectors/SessionSelectors";
import IconBadgePlaceholder from "@pace/XDL/Icons/IconBadgePlaceholder";
import React, { FC, useEffect } from "react";
import { ResolveThunks, connect } from "react-redux";

const mapStateToProps = (state: CustomAppState) => ({
    accountTilesData: state.xAccountTilesData?.data,
    session: getSession(state),
    isAuthenticated: xIsAuthenticated(state),
    cannotViewOrders: xHasRoles(state, ["CannotViewOrders"]),
    cannotViewQuotes: xHasRoles(state, ["CannotViewQuotes"]),
    OrdersPageUrl: getPageLinkByPageType(state, "OrderHistoryPage")?.url,
    QuotesPageUrl: getPageLinkByPageType(state, "RfqMyQuotesPage")?.url,
});

const mapDispatchToProps = {
    loadAccountTilesData,
};

enum fields {
    itemsList = "itemsList",
}
const enum tilesFields {
    type = "type",
    title = "title",
    buttonTitle = "buttonTitle",
    link = "link",
    image = "image",
}
interface AccountTilesListProps {
    fields: {
        [tilesFields.type]: string;
        [tilesFields.title]: string;
        [tilesFields.buttonTitle]: string;
        [tilesFields.link]: LinkFieldValue;
        [tilesFields.image]: string;
    };
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.itemsList]: AccountTilesListProps[];
    };
}

type Props = OwnProps & WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

interface AccountTilesStyles {
    wrapper?: InjectableCss;
    titleSection?: InjectableCss;
    title?: TypographyProps;
    item?: {
        titleContainer?: InjectableCss;
        title?: TypographyPresentationProps;
        link?: LinkPresentationProps;
        number?: TypographyProps;
        image?: LazyImageProps;
        imageWrapper?: InjectableCss;
    };
    itemInfoWrapper?: InjectableCss;
    tilesContainer?: GridContainerProps;
    tileItem?: GridItemProps;
    itemContainer?: InjectableCss;
    greetingTitle?: TypographyProps;
    spinnerWrapper?: InjectableCss;
}

export const accountTilesStyles: AccountTilesStyles = {};

const styles = accountTilesStyles;

const AccountTiles: FC<Props> = ({
    fields,
    session,
    accountTilesData,
    isAuthenticated,
    loadAccountTilesData,
    cannotViewOrders,
    cannotViewQuotes,
    OrdersPageUrl,
    QuotesPageUrl,
}) => {
    if (!isAuthenticated) {
        return null;
    }

    useEffect(() => {
        loadAccountTilesData({});
    }, []);

    const itemsLinks = useGetLinks(fields.itemsList, o => o.fields.link);

    if (!session || !accountTilesData) {
        return (
            <StyledWrapper {...styles.spinnerWrapper}>
                <LoadingSpinner />
            </StyledWrapper>
        );
    }

    return (
        <StyledWrapper {...styles.wrapper} data-test-selector="AccountTiles">
            <StyledWrapper {...styles.titleSection}>
                <Typography {...styles.greetingTitle} data-test-selector="AccountTiles_title">
                    {siteMessage("Custom_AccountTiles_Greeting")}
                    {session.firstName && ","}
                </Typography>
                {session.firstName && (
                    <Typography {...styles.title} data-test-selector="AccountTiles_subtitle">
                        {session.firstName}
                    </Typography>
                )}
            </StyledWrapper>
            <GridContainer {...styles.tilesContainer}>
                {fields.itemsList?.map((itemField, index) => {
                    const item = {
                        link: itemsLinks?.[index].url || "",
                        type: itemField.fields.type,
                        title: itemField.fields.title,
                        image: itemField.fields.image,
                        buttonTitle: itemField.fields.buttonTitle,
                    };
                    if (item.link === OrdersPageUrl && cannotViewOrders) {
                        return null;
                    }
                    if (item.link === QuotesPageUrl && cannotViewQuotes) {
                        return null;
                    }
                    return (
                        <GridItem {...styles.tileItem} key={item.title}>
                            <StyledWrapper {...styles.itemContainer}>
                                <StyledWrapper {...styles.item?.imageWrapper}>
                                    {item.image ? (
                                        <LazyImage
                                            {...styles.item?.image}
                                            src={item.image}
                                            altText={`Account tile icon for ${item.title}`}
                                            data-test-selector="AccountTiles-TileImage"
                                        />
                                    ) : (
                                        <Icon src={IconBadgePlaceholder} width={37} height={37} />
                                    )}
                                </StyledWrapper>
                                <StyledWrapper {...styles.itemInfoWrapper}>
                                    <StyledWrapper {...styles.item?.titleContainer}>
                                        {!!accountTilesData && item.type in accountTilesData && (
                                            <Typography
                                                data-test-selector="AccountTiles-TileCount"
                                                {...styles.item?.number}
                                            >
                                                {accountTilesData[item.type as keyof typeof accountTilesData]}
                                            </Typography>
                                        )}
                                        <Typography {...styles.item?.title}>{item.title}</Typography>
                                    </StyledWrapper>
                                    <Link href={item.link} {...styles.item?.link}>
                                        {item.buttonTitle}
                                    </Link>
                                </StyledWrapper>
                            </StyledWrapper>
                        </GridItem>
                    );
                })}
            </GridContainer>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(AccountTiles),
    definition: {
        group: "Xngage Custom" as any,
        fieldDefinitions: [
            {
                name: fields.itemsList,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields) => {
                    return item.fields.title;
                },
                defaultValue: [],
                fieldType: "General",
                isVisible: item => !item.fields.isAuthenticated,
                fieldDefinitions: [
                    {
                        name: tilesFields.type,
                        displayName: "Type",
                        editorTemplate: "DropDownField",
                        options: [
                            { displayName: "General", value: "General" },
                            { displayName: "Quotes", value: "quotesCount" },
                            { displayName: "Wish Lists", value: "wishListsCount" },
                        ],
                        hideEmptyOption: true,
                        defaultValue: "",
                    },
                    {
                        name: tilesFields.title,
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isRequired: true,
                    },
                    {
                        name: tilesFields.buttonTitle,
                        editorTemplate: "TextField",
                        defaultValue: "View Lists",
                        isRequired: false,
                    },
                    {
                        name: tilesFields.link,
                        displayName: "Destination",
                        editorTemplate: "LinkField",
                        defaultValue: { type: "Url", value: "" },
                        allowCategories: item => false,
                    },
                    {
                        name: tilesFields.image,
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                        isRequired: false,
                    },
                ],
            },
        ],
    },
};

export default widgetModule;
